'use client'

import { useEffect, useState } from 'react'
import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { Box, Typography, Chip, Pagination, Switch, FormControlLabel } from '@mui/material'
import ForumIcon from '@mui/icons-material/Forum'
import type { EthicsFormSubmission } from '../model/types'
import { useFilteredEthicsSubmissions } from '../model/useFilteredEthicsSubmissions'
import { getStatusConfig } from '../lib/statusConfig'
import { getTagLabel } from '../lib/tagLabels'
import { useLocale } from '@/lib/next-shim'
import { EthicsResponseModal } from './EthicsResponseModal'
import { SubmissionCard } from './SubmissionCard'

const PAGE_SIZE = 5

type EthicsSubmissionsListProps = {
  submissions: EthicsFormSubmission[]
}

export function EthicsSubmissionsList({ submissions }: EthicsSubmissionsListProps) {
  const [selectedSubmission, setSelectedSubmission] = useState<EthicsFormSubmission | null>(null)
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [resolvedOnly, setResolvedOnly] = useState(false)
  const { tags, filtered } = useFilteredEthicsSubmissions(submissions, activeTag, resolvedOnly)
  const locale = useLocale()

  const [page, setPage] = useState(1)
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE)
  const pagedSubmissions = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [activeTag, resolvedOnly])

  if (submissions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <ForumIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1.5 }} />
        <Typography sx={{ fontWeight: 500, color: 'text.secondary' }}>
          <Trans>You have not submitted any ethical questions yet.</Trans>
        </Typography>
      </Box>
    )
  }

  return (
    <Box component="section">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <FormControlLabel
          control={<Switch size="small" color="info" checked={resolvedOnly} onChange={(event) => setResolvedOnly(event.target.checked)} />}
          label={
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {getStatusConfig('resolved').label}
            </Typography>
          }
          sx={{ mr: 0 }}
        />
      </Box>

      {tags.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1,
            mb: 4,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 0.75,
            scrollbarWidth: 'thin',
          }}
        >
          <CategoryChip label={t`All`} active={activeTag === null} onClick={() => setActiveTag(null)} />
          {tags.map((tag) => (
            <CategoryChip key={tag} label={getTagLabel(tag, locale)} active={tag === activeTag} onClick={() => setActiveTag(tag === activeTag ? null : tag)} />
          ))}
        </Box>
      )}

      {/* Submission cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {pagedSubmissions.map((submission) => (
          <SubmissionCard key={submission._id} submission={submission} onViewDetail={() => setSelectedSubmission(submission)} />
        ))}
      </Box>

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
        </Box>
      )}

      {selectedSubmission && <EthicsResponseModal submission={selectedSubmission} onClose={() => setSelectedSubmission(null)} />}
    </Box>
  )
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      sx={{
        fontWeight: 600,
        borderRadius: 3,
        px: 1,
        ...(active
          ? { bgcolor: 'info.main', color: 'info.contrastText', '&:hover': { bgcolor: 'info.dark' } }
          : { bgcolor: 'background.default', color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }),
      }}
    />
  )
}
